import { useEffect, useRef } from 'react';

type PollingCallback = () => Promise<boolean>;
type UsePollingOptions = {
  interval: number;
  timeout: number;
  onSuccess: () => void;
  onTimeout?: () => void;
};

export function usePolling(
  shouldStart: boolean,
  callback: PollingCallback,
  options: UsePollingOptions,
) {
  const { interval, timeout, onSuccess, onTimeout } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!shouldStart) return;

    cancelled.current = false;

    const startPolling = async () => {
      const poll = async () => {
        if (cancelled.current) return;

        try {
          const success = await callback();
          if (success) {
            onSuccess();
            return;
          }
          setTimeout(poll, interval);
        } catch (error) {
          setTimeout(poll, interval);
        }
      };

      poll();

      timeoutRef.current = setTimeout(() => {
        cancelled.current = true;
        onTimeout?.();
      }, timeout);
    };

    startPolling();

    return () => {
      cancelled.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStart, interval, timeout, onSuccess]);
}
